import { pct, shortCat, num } from "@/lib/format";
import { crore, terFmt } from "@/lib/amc";
import { PieChart, LineChart, Briefcase, Umbrella, HeartPulse, MapPin, Bitcoin, Coins, ArrowLeftRight } from "lucide-react";

export const COLORS = ["#10B981", "#F59E0B", "#6366F1"];

const fundRows = [
  { l: "Category", f: (x) => shortCat(x.category) },
  { l: "AMC (fund house)", f: (x) => x.amc },
  { l: "Plan", f: (x) => (x.asset_class === "ETF" ? "ETF (exchange traded)" : `${x.plan} · Growth`) },
  { l: "NAV", f: (x) => `₹${num(x.nav, 2)}` },
  { l: "Expense ratio (TER)", v: (x) => x.ter, f: (x) => terFmt(x.ter), best: "min" },
  { l: "Fund size (AUM)", v: (x) => x.aum_cr, f: (x) => crore(x.aum_cr), best: "max" },
  { l: "1 Month", v: (x) => x.r1m, f: (x) => pct(x.r1m), best: "max" },
  { l: "6 Months", v: (x) => x.r6m, f: (x) => pct(x.r6m), best: "max" },
  { l: "1 Year", v: (x) => x.r1y, f: (x) => pct(x.r1y), best: "max" },
  { l: "3Y CAGR", v: (x) => x.r3y, f: (x) => pct(x.r3y), best: "max" },
  { l: "5Y CAGR", v: (x) => x.r5y, f: (x) => pct(x.r5y), best: "max" },
];
const derivRows = (pairsRow) => [
  { l: "Exchanges", f: (x) => x.exchanges }, { l: "Futures brokerage", f: (x) => x.fut_fee }, { l: "Options brokerage", f: (x) => x.opt_fee },
  pairsRow,
  { l: "Active clients (all segments)", v: (x) => x.active_clients_lakh, f: (x) => `~${x.active_clients_lakh} lakh`, best: "max" },
  { l: "App rating", v: (x) => x.rating, f: (x) => `★ ${x.rating}`, best: "max" },
  { l: "Best suited for", f: (x) => x.best_for },
];
const derivVerdict = [
  { l: "Most trusted (clients)", v: (x) => x.active_clients_lakh, best: "max", f: (x) => `~${x.active_clients_lakh} L` },
  { l: "Top rated app", v: (x) => x.rating, best: "max", f: (x) => `★ ${x.rating}` },
  { l: "Lowest futures fee", v: (x) => (x.fut_fee.startsWith("₹20 or") ? 1 : 0), best: "max", f: (x) => x.fut_fee },
];
const fundVerdict = [
  { l: "Best 3-year return", v: (x) => x.r3y, best: "max", f: (x) => pct(x.r3y) },
  { l: "Lowest cost", v: (x) => x.ter, best: "min", f: (x) => terFmt(x.ter) },
  { l: "Largest fund", v: (x) => x.aum_cr, best: "max", f: (x) => crore(x.aum_cr) },
];

export const TYPES = {
  mf: { label: "Mutual Funds", icon: PieChart, noun: "fund", fund: true, glossary: "mf", rows: fundRows, verdict: fundVerdict },
  index: { label: "Index Funds & ETFs", icon: LineChart, noun: "index fund / ETF", article: "an", fund: true, glossary: "index", rows: fundRows, verdict: fundVerdict },
  brokers: {
    label: "Stock Brokers", icon: Briefcase, noun: "broker", glossary: "brokers", cta: "Open Account",
    sub: (x) => `${x.type} · ★ ${x.rating}`,
    rows: [
      { l: "Broker type", f: (x) => x.type }, { l: "Delivery brokerage", f: (x) => x.delivery }, { l: "Intraday brokerage", f: (x) => x.intraday },
      { l: "F&O", f: (x) => x.fno }, { l: "Account opening", f: (x) => x.opening }, { l: "Annual Demat charge", f: (x) => x.amc },
      { l: "Active clients", v: (x) => x.active_clients_lakh, f: (x) => `~${x.active_clients_lakh} lakh`, best: "max" },
      { l: "App rating", v: (x) => x.rating, f: (x) => `★ ${x.rating}`, best: "max" },
      { l: "Platforms", f: (x) => x.platforms }, { l: "Best suited for", f: (x) => x.best_for },
    ],
    verdict: [
      { l: "Most trusted (clients)", v: (x) => x.active_clients_lakh, best: "max", f: (x) => `~${x.active_clients_lakh} L` },
      { l: "Top rated app", v: (x) => x.rating, best: "max", f: (x) => `★ ${x.rating}` },
      { l: "Zero delivery charge", v: (x) => (x.delivery.startsWith("₹0") ? 1 : 0), best: "max", f: (x) => x.delivery },
    ],
  },
  crypto: {
    label: "Crypto Exchanges", icon: Bitcoin, noun: "crypto exchange", platform: "crypto", glossary: "crypto", cta: "Sign up on",
    sub: (x) => `${x.products} · ★ ${x.rating}`,
    rows: [
      { l: "FIU-IND registered", f: (x) => x.fiu }, { l: "Products", f: (x) => x.products },
      { l: "Spot trading fee", f: (x) => x.spot_fee }, { l: "Futures / options fee", f: (x) => x.deriv_fee },
      { l: "Coins / contracts listed", v: (x) => x.coins, f: (x) => `${x.coins}+`, best: "max" },
      { l: "Users", v: (x) => x.users_lakh, f: (x) => `~${x.users_lakh} lakh`, best: "max" },
      { l: "App rating", v: (x) => x.rating, f: (x) => `★ ${x.rating}`, best: "max" },
      { l: "INR deposits", f: (x) => x.inr }, { l: "Best suited for", f: (x) => x.best_for },
    ],
    verdict: [
      { l: "Most coins listed", v: (x) => x.coins, best: "max", f: (x) => `${x.coins}+ coins` },
      { l: "Most users", v: (x) => x.users_lakh, best: "max", f: (x) => `~${x.users_lakh} lakh` },
      { l: "Top rated app", v: (x) => x.rating, best: "max", f: (x) => `★ ${x.rating}` },
    ],
  },
  commodities: {
    label: "Commodity Brokers", icon: Coins, noun: "commodity broker", platform: "commodities", glossary: "commodities", cta: "Open Account",
    sub: (x) => `${x.exchanges} · ★ ${x.rating}`,
    rows: derivRows({ l: "Trading tools", f: (x) => x.margin_tools }), verdict: derivVerdict,
  },
  forex: {
    label: "Forex (Currency) Brokers", icon: ArrowLeftRight, noun: "currency broker", platform: "forex", glossary: "forex", cta: "Open Account",
    sub: (x) => `${x.exchanges} · ★ ${x.rating}`,
    rows: derivRows({ l: "Currency pairs", f: (x) => x.pairs }), verdict: derivVerdict,
  },
  realestate: {
    label: "Real Estate Cities", icon: MapPin, noun: "city", glossary: "realestate", cta: "Explore",
    sub: (x) => `${x.state} · ₹${x.avg_price_sqft.toLocaleString("en-IN")}/sq.ft`,
    rows: [
      { l: "State", f: (x) => x.state },
      { l: "Avg. price / sq.ft", v: (x) => x.avg_price_sqft, f: (x) => `₹${x.avg_price_sqft.toLocaleString("en-IN")}`, best: "min" },
      { l: "Avg. 1-yr growth", v: (x) => x.avg_yoy, f: (x) => `+${x.avg_yoy}%`, best: "max" },
      { l: "Avg. rental yield", v: (x) => x.avg_yield, f: (x) => `${x.avg_yield}%`, best: "max" },
      { l: "Top growth locality", f: (x) => { const l = [...x.localities].sort((a, b) => b.yoy - a.yoy)[0]; return `${l.name} (+${l.yoy}%)`; } },
      { l: "Best rental locality", f: (x) => { const l = [...x.localities].sort((a, b) => b.rental_yield - a.rental_yield)[0]; return `${l.name} (${l.rental_yield}%)`; } },
      { l: "Verified projects", v: (x) => x.projects, f: (x) => x.projects, best: "max" },
    ],
    verdict: [
      { l: "Most affordable", v: (x) => x.avg_price_sqft, best: "min", f: (x) => `₹${x.avg_price_sqft.toLocaleString("en-IN")}/sq.ft` },
      { l: "Fastest growing", v: (x) => x.avg_yoy, best: "max", f: (x) => `+${x.avg_yoy}% a year` },
      { l: "Best rental income", v: (x) => x.avg_yield, best: "max", f: (x) => `${x.avg_yield}% yield` },
    ],
  },
  life: {
    label: "Life Insurance", icon: Umbrella, noun: "life insurer", article: "a", insurer: "life", glossary: "life", cta: "Get Free Quote",
    sub: (x) => `CSR ${x.csr.toFixed(2)}%`,
    rows: [
      { l: "Claim settlement ratio", v: (x) => x.csr, f: (x) => `${x.csr.toFixed(2)}%`, best: "max" },
      { l: "Solvency ratio", v: (x) => x.solvency, f: (x) => x.solvency, best: "max" },
      { l: "Popular plans", f: (x) => x.popular_plans }, { l: "Best suited for", f: (x) => x.best_for },
    ],
    verdict: [
      { l: "Highest claim settlement", v: (x) => x.csr, best: "max", f: (x) => `${x.csr.toFixed(2)}%` },
      { l: "Strongest finances", v: (x) => x.solvency, best: "max", f: (x) => `Solvency ${x.solvency}` },
    ],
  },
  health: {
    label: "Health Insurance", icon: HeartPulse, noun: "health insurer", insurer: "health", glossary: "health", cta: "Get Free Quote",
    sub: (x) => `CSR ${x.csr.toFixed(1)}% · ${x.network_hospitals?.toLocaleString("en-IN")} hospitals`,
    rows: [
      { l: "Claim settlement ratio", v: (x) => x.csr, f: (x) => `${x.csr.toFixed(1)}%`, best: "max" },
      { l: "Incurred claim ratio", f: (x) => `${x.icr}%` },
      { l: "Network hospitals", v: (x) => x.network_hospitals, f: (x) => `${x.network_hospitals?.toLocaleString("en-IN")}+`, best: "max" },
      { l: "Solvency ratio", v: (x) => x.solvency, f: (x) => x.solvency, best: "max" },
      { l: "Popular plans", f: (x) => x.popular_plans }, { l: "Best suited for", f: (x) => x.best_for },
    ],
    verdict: [
      { l: "Highest claim settlement", v: (x) => x.csr, best: "max", f: (x) => `${x.csr.toFixed(1)}%` },
      { l: "Widest hospital network", v: (x) => x.network_hospitals, best: "max", f: (x) => `${x.network_hospitals?.toLocaleString("en-IN")}` },
      { l: "Strongest finances", v: (x) => x.solvency, best: "max", f: (x) => `Solvency ${x.solvency}` },
    ],
  },
};

export const winnerIdx = (items, row) => {
  if (!row.best || items.length < 2) return -1;
  const vals = items.map((x) => row.v(x));
  const valid = vals.filter((v) => v != null);
  if (valid.length < 2 || new Set(valid).size === 1) return -1;
  const target = row.best === "max" ? Math.max(...valid) : Math.min(...valid);
  return vals.indexOf(target);
};

export const keyOf = (x) => x.code || x.id;
